import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import CustomReactSelect from "components/CustomReactSelect";
import DataTable from "components/DataTable";
import HeaderCard from "components/DataTable/HeaderCard";
import Loading from "components/Loading";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import RideSelector from "components/RideSelector";
import dayjs from "dayjs";
import { searchBoxStyles } from "globalStyles";
import debounce from 'lodash/debounce';
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from '@tanstack/react-query';
import { toast } from "react-toastify";
import useFilterStore from "store/useFilterStore";
import { getChecklistCategories, getFrequencyFilters, getStatusFilters } from "utils/helper";

const PAGE_SIZE = 20;
const STATUS_FILTERS = getStatusFilters()
const frequencyTypes = getFrequencyFilters(true)
const checklistCategories = getChecklistCategories();

function BaseTable({
    title,
    columns,
    fetchData,
    createTableRow,
    queryKey,
    queryResult,
    onParamsChange,
    externalParams,
    showAddButton = false,
    addButtonText = "Add New",
    onAddClick,
    showSearch = false,
    searchPlaceholder = "Search...",
    defaultState = {},
    filterConfigs = [],
    showStatusFilter = false,
    showRideFilter = false,
    showFrequencyTypeFilter = false,
    showInspectionCategoryFilter = false,
    onClickRow,
    customButton = null,
    tableOnly = false,
}) {
    const { getFilters, setFilters } = useFilterStore();

    // Get current pathname for storage key
    const currentPath = window.location.pathname;

    // Load filters from Zustand store on component mount
    const getInitialState = useCallback(() => {
        const record = getFilters(currentPath);
        const stored = record?.baseTable || {};
        if (!record.pathname) {
            return {
                page: 1,
                per_page: PAGE_SIZE,
                sort: 'created_at',
                dir: 'desc',
                ...defaultState
            };
        }
        return { ...stored, ...defaultState };
    }, [defaultState, currentPath, getFilters]);


    const initialState = getInitialState();
    const [params, setParams] = useState(initialState);
    const [searchInput, setSearchInput] = useState(initialState.search ?? "");
    const [numberRangeValues, setNumberRangeValues] = useState({});

    const debouncedSetParams = useMemo(() => debounce((id, value) => {
        setParams(prev => ({ ...prev, [id]: value, page: 1 }));
    }, 700), []);

    useEffect(() => {
        return () => {
            debouncedSetParams.cancel();
        };
    }, [debouncedSetParams]);

    useEffect(() => {
        setNumberRangeValues(prev => ({
            ...prev,
            ...params,
        }));
    }, [params]);

    // Lets a parent push filter values (e.g. clicking a calendar day); pass a new object each time
    useEffect(() => {
        if (externalParams) {
            setParams(prev => ({ ...prev, ...externalParams, page: 1 }));
        }
    }, [externalParams]);

    useEffect(() => {
        if (onParamsChange) {
            onParamsChange(params);
        }
    }, [params, onParamsChange]);

    useEffect(() => {
        setSearchInput(params.search ?? "");
    }, [params.search]);

    const renderFilterConfig = (config) => {
        const {
            id,
            label,
            type = 'text',
            options = [],
            placeholder,
            width = 150,
            isMulti = false,
            async = false,
            loadOptions,
            render,
        } = config;

        const value = params[id];
        const boxSx = { flex: 1, minWidth: width };

        const updateValue = (nextValue) => {
            setParams(prev => ({ ...prev, [id]: nextValue ?? undefined, page: 1 }));
        };

        if (type === 'custom' && typeof render === 'function') {
            return (
                <MDBox key={id} sx={boxSx}>
                    {render({ value, onChange: updateValue, params, setParams })}
                </MDBox>
            );
        }

        if (type === 'select') {
            const selectedValue = isMulti
                ? options.filter(option => Array.isArray(value) && value.includes(option.value))
                : options.find(option => option.value === value) || null;

            return (
                <MDBox key={id} sx={boxSx}>
                    <CustomReactSelect
                        label={label}
                        name={id}
                        value={selectedValue}
                        options={options}
                        onChange={(option) => {
                            const nextValue = isMulti
                                ? (option ? option.map(item => item.value) : [])
                                : option?.value;
                            updateValue(nextValue);
                        }}
                        isClearable
                        isMulti={isMulti}
                        async={async}
                        loadOptions={loadOptions}
                        placeholder={placeholder || label}
                    />
                </MDBox>
            );
        }

        if (type === 'numberRange') {
            const minId = config.minId || `${id}_min`;
            const maxId = config.maxId || `${id}_max`;
            const minValue = numberRangeValues[minId] ?? params[minId] ?? '';
            const maxValue = numberRangeValues[maxId] ?? params[maxId] ?? '';
            const minLabel = config.minLabel || 'Min';
            const maxLabel = config.maxLabel || 'Max';
            const rangeBoxSx = { ...boxSx, minWidth: Math.max(boxSx.minWidth || 150, 250) };

            return (
                <MDBox key={id} sx={rangeBoxSx}>
                    {label && (
                        <MDBox component="div" sx={{ fontSize: 12, color: 'text.secondary', mt: -2.5 }}>
                            {label}
                        </MDBox>
                    )}
                    <MDBox sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <MDBox
                            component="input"
                            type="number"
                            placeholder={minLabel}
                            value={minValue}
                            onChange={(e) => {
                                const nextValue = e.target.value;
                                setNumberRangeValues(prev => ({ ...prev, [minId]: nextValue }));
                                debouncedSetParams(minId, nextValue ? Number(nextValue) : undefined);
                            }}
                            sx={{ ...searchBoxStyles, width: '100%', padding: '10px 12px' }}
                        />
                        <MDBox
                            component="input"
                            type="number"
                            placeholder={maxLabel}
                            value={maxValue}
                            onChange={(e) => {
                                const nextValue = e.target.value;
                                setNumberRangeValues(prev => ({ ...prev, [maxId]: nextValue }));
                                debouncedSetParams(maxId, nextValue ? Number(nextValue) : undefined);
                            }}
                            sx={{ ...searchBoxStyles, width: '100%', padding: '10px 12px' }}
                        />
                    </MDBox>
                </MDBox>
            );
        }

        if (type === 'date') {
            return (
                <MDBox key={id} sx={boxSx}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label={label || placeholder}
                            value={value ? dayjs(value) : null}
                            onChange={(newValue) => updateValue(newValue ? newValue.format('YYYY-MM-DD') : undefined)}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    sx: { flex: 1 },
                                }
                            }}
                        />
                    </LocalizationProvider>
                </MDBox>
            );
        }

        return (
            <MDBox key={id} sx={boxSx}>
                <MDBox
                    component="input"
                    placeholder={placeholder || label}
                    sx={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: 1,
                        border: '1px solid rgba(0,0,0,0.2)',
                    }}
                    value={value ?? ''}
                    onChange={(e) => updateValue(e.target.value)}
                />
            </MDBox>
        );
    };

    const listQueryParams = useMemo(() => {
        const { page, per_page, sort, dir, search, ...filters } = params;
        return {
            page,
            perPage: per_page,
            globalSearch: search,
            ...filters,
            sortBy: sort,
            sortOrder: dir,
        };
    }, [params]);

    const resolvedQueryKey = useMemo(() => {
        if (typeof queryKey === 'function') {
            return queryKey(params);
        }

        if (Array.isArray(queryKey)) {
            return [...queryKey, params];
        }

        if (typeof queryKey === 'string' && queryKey.length > 0) {
            return [queryKey, params];
        }

        return [currentPath, params];
    }, [queryKey, currentPath, params]);

    const query = useQuery({
        queryKey: resolvedQueryKey,
        queryFn: () => {
            if (fetchData) {
                return fetchData(listQueryParams);
            }
            return fetchData(params);
        },
        enabled: !queryResult && !!fetchData,
        keepPreviousData: true,
        retry: 1,
    });

    const apiResponse = queryResult?.data ?? query.data;
    const response = useMemo(() => {
        if (!apiResponse) return null;
        if (apiResponse.data && apiResponse.data.data !== undefined) {
            return apiResponse.data;
        }
        return apiResponse;
    }, [apiResponse]);

    const isFetching = queryResult?.isFetching ?? query.isFetching;
    const error = queryResult?.error ?? query.error;
    const isLoading = isFetching;

    const paging = useMemo(() => response?.paging || {
        page: params.page,
        per_page: params.per_page,
        total: 0,
    }, [response, params.page, params.per_page]);

    useEffect(() => {
        if (response?.data) {
            if (currentPath.split('/').length <= 2) {
                setFilters(currentPath, {
                    baseTable: {
                        ...params,
                    }
                });
            }
        }
    }, [response?.data, currentPath, params, setFilters]);

    const tableData = useMemo(() => ({
        columns,
        rows: response?.data
            ?.map(item => ({
                id: item.id,
                oriData: item,
                ...createTableRow(item)
            })) || []
    }), [response, createTableRow, columns]);

    const handleSearchChange = useMemo(() => debounce((value) => {
        setParams(prev => ({ ...prev, search: value, page: 1 }));
    }, 700), []);

    useEffect(() => {
        return () => {
            handleSearchChange.cancel();
        };
    }, [handleSearchChange]);

    useEffect(() => {
        if (error) {
            toast.error(
                error?.data?.message || error?.data?.error || (typeof error === 'string' ? error : 'Failed to fetch data'),
                { toastId: 'tableFetchError' }
            );
        }
    }, [error]);

    const handlePageChange = (newPage) => {
        setParams(prev => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (newSize) => {
        setParams(prev => ({ ...prev, per_page: newSize, page: 1 }));
    };

    const hasActiveFilters = useMemo(() => Object.entries(params).some(([k, v]) =>
        !['page', 'per_page', 'sort', 'dir'].includes(k) && v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)
    ), [params]);

    const handleClearFilters = () => {
        setSearchInput("");
        setParams(prev => ({ page: 1, per_page: prev.per_page, sort: prev.sort, dir: prev.dir }));
    };

    const handleSortChange = (columnId, direction) => {
        setParams(prev => ({ ...prev, sort: columnId, dir: direction, page: 1 }));
    };


    const content = (
        <>
            {!tableOnly && <HeaderCard
                title={title}
                showButton={showAddButton}
                buttonText={addButtonText}
                onButtonClick={onAddClick}
            />}
            <MDBox pt={tableOnly ? 1 : 3}>
                <MDBox mb={3} px={2}>
                    <MDBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, paddingTop: 1 }}>
                        {showSearch && (
                            <MDBox
                                component="input"
                                placeholder={searchPlaceholder}
                                sx={{
                                    flex: 1.2,
                                    ...searchBoxStyles
                                }}
                                value={searchInput}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setSearchInput(value);
                                    handleSearchChange(value);
                                }}
                            />
                        )}
                        {showInspectionCategoryFilter && <MDBox sx={{ flex: 1, minWidth: 190 }}>
                            <CustomReactSelect
                                label="Category"
                                name="category"
                                value={checklistCategories.find(cat => cat.value === params.category)}
                                onChange={option => setParams(prev => ({ ...prev, category: option?.value, page: 1 }))}
                                options={checklistCategories}
                                isClearable
                            />
                        </MDBox>}
                        {showFrequencyTypeFilter && <MDBox sx={{ flex: 1, minWidth: 140 }}>
                            <CustomReactSelect
                                label="Type"
                                name="type"
                                placeholder="All Type"
                                value={frequencyTypes.find(type => type.value === params.type)}
                                onChange={option => setParams(prev => ({ ...prev, type: option?.value, page: 1 }))}
                                options={frequencyTypes}
                                isClearable
                            />
                        </MDBox>}
                        {showRideFilter && <MDBox sx={{ flex: 1.4, minWidth: 200 }}>
                            <RideSelector
                                key={params.ride_id}
                                label="Ride"
                                name="ride"
                                value={params.ride_id}
                                onChange={option => { setParams(prev => ({ ...prev, ride_id: option, page: 1 })) }}
                                isClearable
                            />
                        </MDBox>}
                        {filterConfigs?.length > 0 && filterConfigs.map(renderFilterConfig)}
                        {showStatusFilter && <MDBox sx={{ flex: 1, maxWidth: 200 }}>
                            <CustomReactSelect
                                label="Status"
                                name="status"
                                value={STATUS_FILTERS.find(option => option.value === params.status)}
                                onChange={(selectedOption) => setParams(prev => ({ ...prev, status: selectedOption?.value, page: 1 }))}
                                options={STATUS_FILTERS}
                                isClearable
                            />
                        </MDBox>}
                        {customButton}
                    </MDBox>
                </MDBox>
                <MDBox position="relative" width="100%">
                    {isLoading && (
                        <MDBox
                            position="absolute"
                            top={0}
                            left={0}
                            width="100%"
                            height="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                backgroundColor: 'rgba(255,255,255,0.7)',
                                zIndex: 10,
                                borderRadius: 2,
                            }}
                        >
                            <Loading />
                        </MDBox>
                    )}
                    {paging?.total > 0 ? (
                        <DataTable
                            table={tableData}
                            isSorted={true}
                            noEndBorder
                            page={paging.page}
                            pageSize={paging.per_page}
                            totalCount={paging.total}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            sortBy={params.sort}
                            sortType={params.dir}
                            onSortChange={handleSortChange}
                            onClickRow={onClickRow}
                        />
                    ) : (
                        <MDBox
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            minHeight="200px"
                        >
                            {!isLoading && error && (
                                <>
                                    <MDTypography variant="button" color="error">Failed to load data.</MDTypography>
                                    <MDButton size="small" variant="outlined" color="primary" sx={{ mt: 1 }} onClick={() => (queryResult?.refetch ?? query.refetch)()}>
                                        Retry
                                    </MDButton>
                                </>
                            )}
                            {!isLoading && !error && (
                                <>
                                    <MDTypography variant="button" color="text">No data found</MDTypography>
                                    {hasActiveFilters && (
                                        <MDButton size="small" variant="outlined" color="primary" sx={{ mt: 1 }} onClick={handleClearFilters}>
                                            Clear filters
                                        </MDButton>
                                    )}
                                </>
                            )}
                        </MDBox>
                    )}
                </MDBox>
            </MDBox>
        </>
    );

    return (
        <MDBox pt={4} pb={3}>
            <Grid container spacing={6}>
                <Grid item xs={12}>
                    {tableOnly ? content : <Card>{content}</Card>}
                </Grid>
            </Grid>
        </MDBox>
    );
}

BaseTable.propTypes = {
    title: PropTypes.string,
    columns: PropTypes.array.isRequired,
    fetchData: PropTypes.func,
    createTableRow: PropTypes.func.isRequired,
    showAddButton: PropTypes.bool,
    addButtonText: PropTypes.string,
    onAddClick: PropTypes.func,
    showSearch: PropTypes.bool,
    searchPlaceholder: PropTypes.string,
    showRideFilter: PropTypes.bool,
    showFrequencyTypeFilter: PropTypes.bool,
    defaultState: PropTypes.object,
    storageKey: PropTypes.string,
    onClickRow: PropTypes.func,
    customButton: PropTypes.node,
    queryResult: PropTypes.object,
    onParamsChange: PropTypes.func,
    externalParams: PropTypes.object,
    queryKey: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
    filterConfigs: PropTypes.array,
    tableOnly: PropTypes.bool
};

export default BaseTable;